import {
  Person as PersonIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Home as HomeIcon,
  LocationCity as CityIcon,
  Map as StateIcon,
  MarkunreadMailbox as ZipIcon,
  Public as CountryIcon,
} from '@mui/icons-material';
import { Grid, TextField, InputAdornment } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * AddressForm - Reusable address fields
 *
 * @param {Object} props
 * @param {string} props.prefix - Field name prefix (e.g., 'ship_', 'bill_')
 * @param {Object} props.values - Form values object
 * @param {Object} props.errors - Form errors object
 * @param {Object} props.touched - Form touched object
 * @param {function} props.onChange - Formik handleChange
 * @param {function} props.onBlur - Formik handleBlur
 * @param {boolean} props.disabled - Disable all fields
 * @param {boolean} props.showBusinessName - Show business_name field (default: true)
 * @param {boolean} props.quickMode - If true, only name and email are required (default: false)
 */
const AddressForm = ({
  prefix = '',
  values,
  errors = {},
  touched = {},
  onChange,
  onBlur,
  disabled = false,
  showBusinessName = true,
  quickMode = false,
}) => {
  const { t } = useTranslation();

  const getFieldName = field => `${prefix}${field}`;
  const getFieldValue = field => values[getFieldName(field)] || '';
  const getFieldError = field => {
    const name = getFieldName(field);
    return touched[name] && errors[name];
  };

  return (
    <Grid container spacing={2.5}>
      <Grid size={12}>
        <TextField
          fullWidth
          name={getFieldName('contact')}
          label={t('contacts.contactName')}
          value={getFieldValue('contact')}
          onChange={onChange}
          onBlur={onBlur}
          error={!!getFieldError('contact')}
          helperText={getFieldError('contact')}
          disabled={disabled}
          required
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <PersonIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
      </Grid>

      {showBusinessName && (
        <Grid size={12}>
          <TextField
            fullWidth
            name={getFieldName('company_name')}
            label={t('contacts.businessName')}
            value={getFieldValue('company_name')}
            onChange={onChange}
            onBlur={onBlur}
            disabled={disabled}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <BusinessIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      )}

      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          fullWidth
          name={getFieldName('email')}
          label={t('contacts.email')}
          type="email"
          value={getFieldValue('email')}
          onChange={onChange}
          onBlur={onBlur}
          error={!!getFieldError('email')}
          helperText={getFieldError('email')}
          disabled={disabled}
          required
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <EmailIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          fullWidth
          name={getFieldName('phone')}
          label={t('contacts.phone')}
          value={getFieldValue('phone')}
          onChange={onChange}
          onBlur={onBlur}
          disabled={disabled}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <PhoneIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          fullWidth
          name={getFieldName('address')}
          label={t('contacts.address')}
          value={getFieldValue('address')}
          onChange={onChange}
          onBlur={onBlur}
          error={!!getFieldError('address')}
          helperText={getFieldError('address')}
          disabled={disabled}
          required={!quickMode}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <HomeIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          fullWidth
          name={getFieldName('address_2')}
          label={t('contacts.address2', 'Apt / Suite / Unit')}
          value={getFieldValue('address_2')}
          onChange={onChange}
          onBlur={onBlur}
          error={!!getFieldError('address_2')}
          helperText={getFieldError('address_2')}
          disabled={disabled}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <HomeIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          fullWidth
          name={getFieldName('city')}
          label={t('contacts.city')}
          value={getFieldValue('city')}
          onChange={onChange}
          onBlur={onBlur}
          error={!!getFieldError('city')}
          helperText={getFieldError('city')}
          disabled={disabled}
          required={!quickMode}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <CityIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 3 }}>
        <TextField
          fullWidth
          name={getFieldName('state')}
          label={t('contacts.state')}
          value={getFieldValue('state')}
          onChange={onChange}
          onBlur={onBlur}
          error={!!getFieldError('state')}
          helperText={getFieldError('state')}
          disabled={disabled}
          required={!quickMode}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <StateIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 3 }}>
        <TextField
          fullWidth
          name={getFieldName('zip')}
          label={t('contacts.zipCode')}
          value={getFieldValue('zip')}
          onChange={onChange}
          onBlur={onBlur}
          error={!!getFieldError('zip')}
          helperText={getFieldError('zip')}
          disabled={disabled}
          required={!quickMode}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <ZipIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
      </Grid>

      <Grid size={12}>
        <TextField
          fullWidth
          name={getFieldName('country')}
          label={t('contacts.country')}
          value={getFieldValue('country')}
          onChange={onChange}
          onBlur={onBlur}
          disabled={disabled}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <CountryIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
      </Grid>
    </Grid>
  );
};

export default AddressForm;
